'use client'

import { alert } from '@repo/ui/Alert'
import { CaseManagementIcon } from '@repo/ui/CaseManagementIcon'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { DownloadIcon } from '@repo/ui/DownloadIcon'
import { formatThousands } from '@repo/utils/numbers'
import { GridToolbarColumnsButton } from '@mui/x-data-grid-pro'
import { KeyDateIcon } from '@repo/ui/KeyDateIcon'
import { useCallback, useState } from 'react'
import { useClientTableStore } from '@repo/store/useClientTableStore'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useDocumentBundleWizardStore } from '@repo/store/useDocumentBundleWizardStore'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useMatterBundleStore } from '@repo/store/useMatterBundleStore'
import { useMatterTableStore } from '@repo/store/useMatterTableStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useNavigate } from 'react-router-dom'
import { UserIcon } from '@repo/ui/UserIcon'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIStack from '@mui/material/Stack'
import PropTypes from 'prop-types'
import { DocumentIcon } from '@repo/ui/DocumentIcon'

type CustomToolbarTableModuleProps = {
  isMatterTable?: boolean
  isKeyDatesTable?: boolean
  isMatterSpecific?: boolean
  isDocumentBundlesTable?: boolean
  apiRef?: any
}

const BUNDLE_CSV_COLUMNS = [
  { field: 'matterNo', header: 'Matter', getValue: (row: any) => `${row.clientNo || ''}-${row.matterNo || ''}` },
  {
    field: 'description',
    header: 'Description',
    getValue: (row: any) => `"${(row.description || '').replace(/"/g, '""')}"`
  },
  {
    field: 'createdDate',
    header: 'Created date',
    getValue: (row: any) => (row.createdDate ? row.createdDate.slice(0, 10) : '')
  },
  { field: 'createdBy', header: 'Created by', getValue: (row: any) => row.createdBy || '' }
]

export const CustomToolbarTableModule = ({
  isMatterTable,
  isKeyDatesTable,
  isMatterSpecific,
  isDocumentBundlesTable,
  apiRef
}: CustomToolbarTableModuleProps) => {
  const [mainAnchorEl, setMainAnchorEl] = useState<HTMLElement | null>(null)

  // Get both branches from the store.
  const { mattersTableFilters, clientsTableFilters, keyDatesTableFilters, matterSpecificKeyDatesTableFilters, documentBundlesTableFilters } =
    useDataGridFilterStore()

  // For convenience, choose the active filters depending on the table type.
  const activeFilters = isMatterTable
    ? mattersTableFilters
    : isKeyDatesTable
      ? keyDatesTableFilters
      : isMatterSpecific
        ? matterSpecificKeyDatesTableFilters
        : isDocumentBundlesTable
          ? documentBundlesTableFilters
          : clientsTableFilters

  // The CSV export functions remain the same.
  const { fetchClientsTableAsCSV } = useClientTableStore()
  const { fetchMattersTableAsCSV } = useMatterTableStore()
  const { fetchKeyDatesTableDataAsCSV, toggleKeyDatesDrawer } = useKeyDateStore()
  const { matterBundles } = useMatterBundleStore()
  const { reset: resetDocumentBundleWizardStore } = useDocumentBundleWizardStore()
  const { enableBundledocs } = useLookupsStore()

  const { allowedMenuIds } = useMenuItemsStore()

  const navigate = useNavigate()

  const openMainMenu = Boolean(mainAnchorEl)

  const handleMainButtonClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMainAnchorEl(event.currentTarget)
  }, [])

  const handleCloseAll = useCallback(() => {
    setMainAnchorEl(null)
  }, [])

  const handleNewBundle = () => {
    resetDocumentBundleWizardStore()
    navigate('?wizard=DocumentBundle&stage=create-bundle&step=create-document-bundle')
    handleCloseAll()
  }

  const handleAddNew = useCallback(() => {
    if (isMatterTable) {
      navigate('?wizard=COIS-NewClient-NewMatter&stage=conflict&step=introduction')
    } else if (isKeyDatesTable) {
      toggleKeyDatesDrawer({
        keyDatesDrawerIsOpen: true,
        keyDatesDrawerMode: 'create'
      })
    } else if (isDocumentBundlesTable) {
      // Create bundle is out of scope for this ticket — no-op
    } else {
      navigate('?wizard=NewClient-NewMatter&stage=client&step=client-details')
    }
    handleCloseAll()
  }, [isMatterTable, isKeyDatesTable, isDocumentBundlesTable, navigate, handleCloseAll, toggleKeyDatesDrawer])

  const exportBundlesAsCsv = useCallback(() => {
    try {
      const rows = matterBundles || []
      if (!rows.length) return

      // Only include columns the user hasn't explicitly hidden via the column menu.
      const visibleColumns = apiRef?.current
        ? (() => {
            const visibleFields = apiRef.current.getVisibleColumns().map((column: any) => column.field)
            return BUNDLE_CSV_COLUMNS.filter((col) => visibleFields.includes(col.field))
          })()
        : BUNDLE_CSV_COLUMNS

      const headers = visibleColumns.map((col) => col.header)
      const csvRows = rows.map((row: any) => visibleColumns.map((col) => col.getValue(row)))

      const csvContent = [headers.join(','), ...csvRows.map((r: string[]) => r.join(','))].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = 'document_bundles_export.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error('Error exporting bundles as CSV:', err)
    }
  }, [matterBundles, apiRef])

  const handleExportCsv = useCallback(async () => {
    if (isMatterTable) {
      handleCloseAll()
      fetchMattersTableAsCSV({
        orderByColumns: mattersTableFilters.orderByColumns,
        pageSize: mattersTableFilters.pageSize,
        startAtIndex: mattersTableFilters.startAtIndex,
        filters: mattersTableFilters.filters
      })
    } else if (isDocumentBundlesTable) {
      handleCloseAll()
      exportBundlesAsCsv()
    } else if (isMatterSpecific) {
      handleCloseAll()
      fetchKeyDatesTableDataAsCSV({
        keyDatesTableFilters: matterSpecificKeyDatesTableFilters
      })
    } else if (isKeyDatesTable) {
      handleCloseAll()
      fetchKeyDatesTableDataAsCSV({
        keyDatesTableFilters: keyDatesTableFilters
      })
    } else {
      handleCloseAll()
      fetchClientsTableAsCSV({
        orderByColumns: clientsTableFilters.orderByColumns,
        pageSize: clientsTableFilters.pageSize,
        startAtIndex: clientsTableFilters.startAtIndex,
        filters: clientsTableFilters.filters
      })
    }
  }, [
    clientsTableFilters,
    exportBundlesAsCsv,
    fetchClientsTableAsCSV,
    fetchKeyDatesTableDataAsCSV,
    fetchMattersTableAsCSV,
    handleCloseAll,
    isDocumentBundlesTable,
    isMatterTable,
    keyDatesTableFilters,
    mattersTableFilters
  ])

  const disableCSVDownload = () => {
    if (isKeyDatesTable || isDocumentBundlesTable) {
      return false
    } else if (isMatterTable) {
      return !allowedMenuIds.includes(4610)
    } else {
      return !allowedMenuIds.includes(4870)
    }
  }

  return (
    <MUIStack
      display="flex"
      justifyContent="center"
      alignItems="flex-end"
      sx={{ marginRight: '1rem', position: 'relative' }}
    >
      <MUIButton
        aria-haspopup="true"
        aria-controls={openMainMenu ? 'main-actions-menu' : undefined}
        variant="contained"
        color="primary"
        onClick={handleMainButtonClick}
        sx={{
          backgroundColor: '#F5F6F8',
          border: '1px solid rgba(5, 28, 51, 0.23)',
          color: '#051C33',
          display: 'flex',
          fontSize: '0.75rem',
          mb: '1rem',
          padding: '4px 4px 4px 12px',
          '&:hover': { backgroundColor: '#F5F6F8' }
        }}
      >
        Actions
        <ChevronRightIcon
          sx={{ transform: openMainMenu ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }}
        />
      </MUIButton>

      <MUIMenu
  id="main-actions-menu"
  anchorEl={mainAnchorEl}
  open={openMainMenu}
  onClose={handleCloseAll}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
  sx={{
    ml: activeFilters.pageSize > 10 ? '-1rem' : '0',
    [`& .MuiPaper-root`]: { width: '230px', position: 'relative !important' },
    [`& .MuiMenuItem-root`]: { width: '212px' }
  }}
>
  {!isDocumentBundlesTable && (
    <MUIMenuItem
      onClick={handleAddNew}
      sx={{ cursor: 'pointer', height: 46, width: 230, mr: '8px', mb: '4px' }}
      disabled={!allowedMenuIds.includes(1300)}
    >
      {isMatterTable ? (
        <CaseManagementIcon sx={{ height: 18, width: 18, mr: 2 }} />
      ) : isKeyDatesTable ? (
        <KeyDateIcon sx={{ height: 18, width: 18, mr: 2 }} />
      ) : (
        <UserIcon sx={{ height: 18, width: 18, mr: 2, ml: '2px' }} />
      )}
      {isMatterTable ? 'New matter' : isKeyDatesTable ? 'New key date' : 'New client'}
    </MUIMenuItem>
  )}

  {isDocumentBundlesTable && enableBundledocs && (
    <MUIMenuItem
      onClick={handleNewBundle}
      sx={{ cursor: 'pointer', height: 46, width: 230, mr: '8px', mb: '4px' }}
    >
      <DocumentIcon sx={{ height: 18, width: 18, mr: 2 }} />
      New bundle
    </MUIMenuItem>
  )}

  <MUIMenuItem sx={{ cursor: 'pointer', height: 46, width: 230 }}>
    <MUIBox
      sx={{
        display: 'flex',
        alignItems: 'center',
        [`& > button`]: {
          color: 'rgba(5, 28, 51, 0.87)',
          gap: '2px',
          ml: '-3px',
          mt: '4px',
          pt: 0,
          pb: 0,
          pr: 0,
          textTransform: 'capitalize !important',
          width: '15rem',
          justifyContent: 'flex-start',
          height: '48px',
          paddingLeft: '5px'
        },
        [`& > span`]: { marginRight: '3px' },
        [`& > svg`]: { fontSize: '24px' }
      }}
    >
      <GridToolbarColumnsButton />
    </MUIBox>
  </MUIMenuItem>

  <MUIMenuItem
    onClick={handleExportCsv}
    disabled={disableCSVDownload()}
    sx={{
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'flex-start',
      height: 46,
      width: 230
    }}
  >
    <DownloadIcon sx={{ height: 18, width: 18, mr: 2, ml: '0' }} />
    Export as CSV
  </MUIMenuItem>
</MUIMenu>
    </MUIStack>
  )
}

CustomToolbarTableModule.propTypes = {
  isMatterTable: PropTypes.bool,
  isKeyDatesTable: PropTypes.bool,
  isMatterSpecific: PropTypes.bool,
  isDocumentBundlesTable: PropTypes.bool,
  apiRef: PropTypes.object
}